import React, { useState, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  useGetStoreStocksQuery,
  useCreateStoreStockMutation,
} from '../../slices/storeStocksApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

const StoreStockBulkEditScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editableStoreStocks, setEditableStoreStocks] = useState([]);

  const { data: storeStocksData, isLoading, error } = useGetStoreStocksQuery();
  const [createStoreStock] = useCreateStoreStockMutation();

  // Load and prepare editable storeStock data
  useEffect(() => {
    if (storeStocksData?.storeStocks) {
      const prepared = storeStocksData.storeStocks.map((storeStock) => ({
        ...storeStock,
        storeStockItems: storeStock.storeStockItems.map((item) => ({
          ...item,
          qty: Number(item.qty),
          price: Number(item.price),
          total: Number(item.qty) * Number(item.price),
        })),
      }));
      setEditableStoreStocks(prepared);
    }
  }, [storeStocksData]);

  // Filtered view based on search term
  const filteredStoreStocks = editableStoreStocks.filter(
    (storeStock) =>
      storeStock.storeStockItems.some((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || (storeStock.note || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle quantity change
  const handleQtyChange = (storeStockId, itemName, newQty) => {
    setEditableStoreStocks((prev) =>
      prev.map((storeStock) =>
        storeStock._id === storeStockId
          ? {
              ...storeStock,
              storeStockItems: storeStock.storeStockItems.map((item) =>
                item.name === itemName
                  ? {
                      ...item,
                      qty: Number(newQty),
                      total: Number(newQty) * item.price,
                    }
                  : item
              ),
            }
          : storeStock
      )
    );
  };

  // Handle price change
  const handlePriceChange = (storeStockId, itemName, newPrice) => {
    setEditableStoreStocks((prev) =>
      prev.map((storeStock) =>
        storeStock._id === storeStockId
          ? {
              ...storeStock,
              storeStockItems: storeStock.storeStockItems.map((item) =>
                item.name === itemName
                  ? {
                      ...item,
                      price: Number(newPrice),
                      total: item.qty * Number(newPrice),
                    }
                  : item
              ),
            }
          : storeStock
      )
    );
  };

  // Export filtered data to Excel
  const exportToExcel = () => {
    const rows = filteredStoreStocks.flatMap((storeStock) =>
      storeStock.storeStockItems
        .filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((item) => ({
          Date: new Date(storeStock.date).toLocaleDateString(),
          Product: item.name,
          Price: item.price,
          Quantity: item.qty,
          Total: item.total.toFixed(2),
          Note: storeStock.note || 'N/A',
        }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FilteredStoreStocks');
    XLSX.writeFile(workbook, `FilteredStoreStocks_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Submit updates to backend
  const updateToBackend = async () => {
    try {
      for (const storeStock of filteredStoreStocks) {
        const updatedItems = storeStock.storeStockItems.filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (updatedItems.length > 0) {
          await createStoreStock({
            _id: storeStock._id,
            user: storeStock.user,
            date: storeStock.date,
            note: storeStock.note,
            customerDetails: storeStock.customerDetails,
            status: storeStock.status,
            storeStockItems: updatedItems,
          }).unwrap();
          toast.success(`Updated storeStock on ${new Date(storeStock.date).toLocaleDateString()}`);
        }
      }
      toast.success('Matching storeStock records updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update one or more storeStock records');
    }
  };

  return (
    <>
      <h1>Bulk Edit MiniStock Records</h1>

      <Form.Group controlId="searchTerm" className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search by product name or note"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Form.Group>

      <div className="mb-3">
        <Button variant="success" className="me-2" onClick={exportToExcel}>
          Export to Excel
        </Button>
        <Button variant="primary" onClick={updateToBackend} disabled={filteredStoreStocks.length === 0}>
          Update Matching Records
        </Button>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Failed to load storeStock data'}</Message>
      ) : filteredStoreStocks.length === 0 ? (
        <Message variant="info">No matching storeStock records found</Message>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Total</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {filteredStoreStocks.map((storeStock) =>
              storeStock.storeStockItems
                .filter((item) =>
                  item.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((item) => (
                  <tr key={`${storeStock._id}-${item.name}`}>
                    <td>{new Date(storeStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.price}
                        onChange={(e) =>
                          handlePriceChange(storeStock._id, item.name, e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.qty}
                        onChange={(e) =>
                          handleQtyChange(storeStock._id, item.name, e.target.value)
                        }
                      />
                    </td>
                    <td>${item.total.toFixed(2)}</td>
                    <td>{storeStock.note || 'N/A'}</td>
                  </tr>
                ))
            )}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default StoreStockBulkEditScreen;
